import type { OnboardingDataPoint, OnboardingStage } from "./onboardingData";
import homebuilderSitePlan from "@/assets/homebuilder-site-plan.png";
import homebuilderFloorElectrical from "@/assets/homebuilder-floor-electrical.png";
import homebuilderStructuralSection from "@/assets/homebuilder-structural-section.png";
import homebuilderArchitectural from "@/assets/homebuilder-architectural.png";

const sitePlanDataPoints: OnboardingDataPoint[] = [
  { key: "hb-lot-size", label: "Min Lot Size", jurisdiction: "city", typical: true, visualPosition: { x: 85, y: 8 }, summary: "Min 5,000 sqft" },
  { key: "hb-front-setback", label: "Front Yard Setback", jurisdiction: "city", typical: true, visualPosition: { x: 30, y: 90 }, summary: "10 ft from front property line" },
  { key: "hb-side-setback", label: "Side Yard Setback", jurisdiction: "city", typical: true, visualPosition: { x: 8, y: 50 }, summary: "5 ft each side" },
  { key: "hb-rear-setback", label: "Rear Yard Setback", jurisdiction: "city", typical: true, visualPosition: { x: 60, y: 18 }, summary: "15 ft from rear property line" },
  { key: "hb-impervious", label: "Impervious Coverage", jurisdiction: "city", typical: true, visualPosition: { x: 55, y: 55 }, summary: "Max 15% of lot" },
  {
    key: "hb-trees", label: "Tree Requirements", jurisdiction: "city", typical: true, visualPosition: { x: 78, y: 14 },
    summary: "Native species, preservation plan required",
    details: [
      "Acceptable Species: preferably native, similar to onsite vegetation, max 25% single species",
      "Canopy/Density: based on net site acreage; min density required unless infeasible",
      "Existing Tree Size for Credit: deciduous ≥8 in. diameter, conifers ≥4 in. diameter",
      "Permit Requirements: tree preservation & reforestation plan approved by City Forester and Council",
      "Removal Threshold: up to 50% of significant tree inches removable without reforestation",
      "Replacement Tree Size: deciduous min 2.5 in. diameter, conifers min 6 ft height",
    ],
  },
  { key: "hb-parking", label: "Parking Requirements", jurisdiction: "city", typical: true, visualPosition: { x: 62, y: 88 }, summary: "2 spaces/dwelling + 1 per bedroom over 3" },
  {
    key: "hb-fence", label: "Fence Requirements", jurisdiction: "city", typical: true, visualPosition: { x: 96, y: 50 },
    summary: "4 ft front · 10 ft side/rear",
    details: [
      "Front yard max height: 4 ft",
      "Side / rear yard max height: 10 ft",
      "Over 6 ft must meet structural requirements",
    ],
  },
  {
    key: "hb-retaining", label: "Retaining Walls", jurisdiction: "city", typical: true, visualPosition: { x: 10, y: 78 },
    summary: "Front max 4 ft · side/rear max 6 ft",
    details: [
      "Front yard retaining walls: max 4 ft height",
      "Side & rear yard retaining walls: max 6 ft height",
      "Walls above 4 ft require engineered design",
    ],
  },
  {
    key: "hb-accessory", label: "Accessory Structures", jurisdiction: "city", typical: true, visualPosition: { x: 15, y: 8 },
    summary: "Setback rules vary by size & height",
    details: [
      "Decks/Sheds ≤200 sq ft AND ≤8 ft height: may encroach rear, side, street-side setbacks",
      "Decks/Sheds >200 sq ft OR >8 ft height: 3 ft side/rear setback + 1 ft per ft over 9 ft height",
      "No encroachment allowed in front yard",
    ],
  },
  { key: "hb-drawing-standards", label: "Drawing Standards", jurisdiction: "city", typical: true, visualPosition: { x: 95, y: 30 }, nonSpatial: true, summary: "Required sheet, scale & symbol standards" },
  { key: "hb-file-specs", label: "File Specs & Naming", jurisdiction: "city", typical: true, visualPosition: { x: 95, y: 38 }, nonSpatial: true, summary: "PDF format, naming conventions for submittal" },
  { key: "hb-jurisdictional-process", label: "Jurisdictional Process", jurisdiction: "city", typical: true, visualPosition: { x: 95, y: 46 }, nonSpatial: true, summary: "Step-by-step submittal & review workflow" },
  { key: "hb-backflow-prevention", label: "Backflow Prevention", jurisdiction: "city", typical: false },
  { key: "hb-max-building-height", label: "Max Building Height", jurisdiction: "city", typical: false },
  { key: "hb-setback-encroachment", label: "Setback Encroachment Allowances", jurisdiction: "city", typical: false },
];

const floorElectricalDataPoints: OnboardingDataPoint[] = [
  { key: "hb-electrical-code-edition", label: "Electrical Code Edition", jurisdiction: "state", typical: true, nonSpatial: true, visualPosition: { x: 50, y: 8 }, summary: "2020 National Electrical Code" },
  {
    key: "hb-min-floor-area", label: "Minimum Floor Area", jurisdiction: "city", typical: true, visualPosition: { x: 22, y: 22 },
    summary: "Varies by residential zone (1,200–1,800 sq ft)",
    details: [
      "R-1 Single-Family Residential / Agriculture: 1,800 sq ft",
      "R-2 Single-Family Residential: 1,600 sq ft",
      "R-3 Single-Family Residential: 1,400 sq ft",
      "R-4 Single-Family Residential: 1,200 sq ft",
    ],
  },
  { key: "hb-far", label: "Floor Area Ratio", jurisdiction: "city", typical: true, visualPosition: { x: 70, y: 30 }, summary: "Max FAR: 0.3" },
  {
    key: "hb-bath-clearances", label: "Bathroom Clearances", jurisdiction: "state", typical: true, visualPosition: { x: 8, y: 48 },
    summary: "Water closet, sink & tub min clearances",
    details: [
      "Water Closet: 18 in. minimum in front; 56 in. minimum from rear wall",
      "Sink: 15 in. side clearance",
      "Bathtub / Shower: 30 in. × 48 in. maneuvering clearance",
    ],
  },
  {
    key: "hb-gfci", label: "GFCI Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 84, y: 52 },
    summary: "Required for 125–250V receptacles in living, garage, wet areas",
    details: [
      "Mandatory for all 125V through 250V receptacles",
      "Applies to living areas, garages, damp and wet locations",
    ],
  },
  {
    key: "hb-receptacles", label: "Receptacle Spacing", jurisdiction: "state", typical: true, visualPosition: { x: 48, y: 22 },
    summary: "Kitchen: 6 ft max spacing along countertops",
    details: [
      "Kitchen: 6 ft max spacing",
      "Adjacent to countertops, on each side of sink (if > 12 in.)",
      "Adjacent to fridge and gas range spaces",
    ],
  },
  {
    key: "hb-grounding", label: "Grounding Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 78, y: 78 },
    summary: "Cold water bond + ground rod per panel",
    details: ["Water bond to cold water pipe", "New ground rod for each distribution panel"],
  },
  {
    key: "hb-service-panel", label: "Service Panel Sizing", jurisdiction: "city", typical: true, visualPosition: { x: 70, y: 88 },
    summary: "New build: 200A / 240V, 32-space panel",
    details: ["New Build: 200A / 240V, 32-space panel", "Upgrade or retrofit: existing service amperage"],
  },
  { key: "hb-plumbing-code", label: "Plumbing Code Edition", jurisdiction: "state", typical: true, visualPosition: { x: 95, y: 18 }, nonSpatial: true, summary: "2021 Uniform Plumbing Code" },
  { key: "hb-mechanical-code", label: "Mechanical Code Edition", jurisdiction: "state", typical: true, visualPosition: { x: 95, y: 26 }, nonSpatial: true, summary: "2021 Uniform Mechanical Code" },
  { key: "hb-residential-code", label: "Residential Code Edition", jurisdiction: "state", typical: true, visualPosition: { x: 95, y: 34 }, nonSpatial: true, summary: "2021 International Residential Code" },
  {
    key: "hb-contractor-licensing", label: "Contractor Licensing", jurisdiction: "state", typical: true, visualPosition: { x: 95, y: 42 }, nonSpatial: true,
    summary: "State 'B' or specialty 'C' license + local + bonding",
    details: [
      "State: 'B' General Building Contractor or specialty 'C' license",
      "Local: business license from county",
      "Bonding: corporate surety bond until construction completion",
    ],
  },
];

const structuralSectionDataPoints: OnboardingDataPoint[] = [
  { key: "hb-wind-speed", label: "Wind Speed", jurisdiction: "city", typical: true, visualPosition: { x: 88, y: 18 }, summary: "95 mph design wind speed" },
  { key: "hb-snow-load", label: "Snow Load", jurisdiction: "city", typical: true, visualPosition: { x: 50, y: 8 }, summary: "20 psf roof snow load" },
  { key: "hb-roofing", label: "Roofing Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 28, y: 18 }, summary: "Class-A fire rated material" },
  {
    key: "hb-decking", label: "Decking Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 65, y: 30 },
    summary: "1/2 in plywood min · 12 in edges / 24 in field",
    details: [
      "1/2 inch plywood minimum",
      "Attached to structure at 12\" spacing along edges",
      "24\" spacing in center of board",
    ],
  },
  { key: "hb-insulation", label: "Insulation Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 42, y: 42 }, summary: "R-38 ceiling insulation" },
  {
    key: "hb-stairs", label: "Stair Requirements", jurisdiction: "state", typical: true, visualPosition: { x: 15, y: 70 },
    summary: "Riser 7–7¾ in · Tread 9–11 in · Headroom 6'8\"",
    details: [
      "Riser Height: 7 – 7 3/4 inches",
      "Tread Depth: 9 – 11 inches",
      "Allowable variation: 3/8 inches",
      "Minimum headroom clearance: 6 ft 8 in",
    ],
  },
];

const approvalsDataPoints: OnboardingDataPoint[] = [
  { key: "hb-permit-fee", label: "Permit Fee", jurisdiction: "city", typical: true },
  { key: "hb-review-timeline", label: "Review Timeline", jurisdiction: "city", typical: true },
  { key: "hb-expedited-permitting", label: "Expedited Permitting", jurisdiction: "city", typical: true },
  { key: "hb-plan-set-requirements", label: "Plan Set Requirements", jurisdiction: "city", typical: true },
  { key: "hb-inspection-requirements", label: "Inspection Requirements", jurisdiction: "city", typical: true },
  { key: "hb-ahj-contact", label: "AHJ Contact Info", jurisdiction: "city", typical: true },
  { key: "hb-structural-stamp", label: "Structural Stamp Required", jurisdiction: "city", typical: true },
  { key: "hb-electrical-stamp", label: "Electrical Stamp Required", jurisdiction: "city", typical: true },
  { key: "hb-minor-subdivision", label: "Minor Subdivision Submittal Guidelines", jurisdiction: "city", typical: false },
];

const architecturalDataPoints: OnboardingDataPoint[] = [
  {
    key: "hb-facade", label: "Facade Requirements", jurisdiction: "city", typical: true, visualPosition: { x: 35, y: 45 },
    summary: "Articulation, planes, colors & approved materials",
    details: [
      "Street facing: one horizontal or vertical projection or recess, min 4 ft depth, to articulate the facade",
      "Continuous plane: 25 ft maximum",
      "Continuous facade stretch: 15 ft maximum",
      "Colors: 3 minimum",
      "Materials: stone, brick, wood, terracotta, concrete, stucco, fiber cement",
    ],
  },
  { key: "hb-fenestration", label: "Fenestration Requirements", jurisdiction: "city", typical: true, visualPosition: { x: 14, y: 70 }, summary: "15% min on 1st story at front of house" },
  {
    key: "hb-window-door-performance", label: "Window & Door Performance", jurisdiction: "state", typical: true, visualPosition: { x: 60, y: 72 },
    summary: "Air leakage ≤ 0.3 cfm/ft² · wind resistance per IRC/ASCE 7",
    details: [
      "Air Leakage: ≤ 0.3 cfm/ft²",
      "Wind Resistance: withstand design wind loads per IRC Table R301.2.1(1) & ASCE 7",
    ],
  },
];

export const HOMEBUILDER_DIAGRAMS: Record<string, string> = {
  "hb-site-plan": homebuilderSitePlan,
  "hb-floor-electrical": homebuilderFloorElectrical,
  "hb-structural-section": homebuilderStructuralSection,
  "hb-architectural": homebuilderArchitectural,
};

const tagIndustry = (points: OnboardingDataPoint[]): OnboardingDataPoint[] =>
  points.map(p => ({ ...p, industryIds: ["residential-builder"] }));

export const HOMEBUILDER_STAGES: OnboardingStage[] = [
  { id: "hb-site-plan", name: "Site Plan", heading: "Here's the regulatory data homebuilders typically pull for the site plan.", subHeading: "Adjust the list to match your process — we'll use it to build your dataset.", dataPoints: tagIndustry(sitePlanDataPoints) },
  { id: "hb-floor-electrical", name: "Floor & Electrical Plan", heading: "Now layer in the data your team needs for floor & electrical plans.", subHeading: "Adjust the list to match your process — we'll keep adding to your dataset.", dataPoints: tagIndustry(floorElectricalDataPoints) },
  { id: "hb-structural-section", name: "Structural Section Plan", heading: "Add the data your team needs for the structural section plan.", subHeading: "Adjust the list to match your process — we'll keep adding to your dataset.", dataPoints: tagIndustry(structuralSectionDataPoints) },
  { id: "hb-architectural", name: "Architectural Plan", heading: "Finish with the data your team needs for the architectural plan.", subHeading: "Adjust the list to match your process — this completes your dataset.", dataPoints: tagIndustry(architecturalDataPoints) },
  { id: "hb-approvals", name: "Approvals", heading: "Approvals are handled for you", subHeading: "We automatically provide the processes and requirements for Permitting.", dataPoints: tagIndustry(approvalsDataPoints) },
];

export const ALL_HOMEBUILDER_DATA_POINTS = HOMEBUILDER_STAGES.flatMap(s => s.dataPoints);
